import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import RichTextEditor from '../../components/RichTextEditor';
import { useData } from '../../context/DataContext';
import { moduleMaterialsAPI } from '../../services/api';
import './ModuleMaterials.css';

const emptyForm = {
  studyId: '',
  courseId: '',
  title: '',
  description: '',
  type: 'document',
  articleContent: '',
  externalUrl: '',
  sortOrder: 0,
  isActive: true
};

const parseBool = (value) => String(value) === 'true';

const ModuleMaterials = () => {
  const { studies } = useData();

  const [materials, setMaterials] = useState([]);
  const [filterStudy, setFilterStudy] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadFile, setUploadFile] = useState(null);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  // Study option label: "Studie Cohort (code)"
  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const studiesWithCourses = useMemo(() => {
    return (studies || []).filter((study) =>
      (study.studyYears || []).some((year) =>
        (year.modules || []).some((mod) => (mod.courses || []).length > 0)
      )
    );
  }, [studies]);

  // Courses (vakken) available for the page filter, narrowed by selected study
  const filterCourseOptions = useMemo(() => {
    const sourceStudies = filterStudy
      ? studiesWithCourses.filter((s) => s.id === filterStudy)
      : studiesWithCourses;
    const seen = new Set();
    const list = [];
    sourceStudies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((mod) => {
          (mod.courses || []).forEach((course) => {
            const cid = course.id || course._id;
            if (!seen.has(cid)) {
              seen.add(cid);
              list.push({ id: cid, name: course.name, code: course.code });
            }
          });
        });
      });
    });
    return list;
  }, [filterStudy, studiesWithCourses]);

  // NOTE: filterModuleOptions and formModuleOptions removed — materials now link to course (vak), not module

  // Courses (vakken) for the modal form, narrowed by form.studyId
  const formCourseOptions = useMemo(() => {
    const sourceStudies = form.studyId
      ? studiesWithCourses.filter((s) => s.id === form.studyId)
      : studiesWithCourses;
    const seen = new Set();
    const list = [];
    sourceStudies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((mod) => {
          (mod.courses || []).forEach((course) => {
            const cid = course.id || course._id;
            if (!seen.has(cid)) {
              seen.add(cid);
              list.push({ id: cid, name: course.name, code: course.code });
            }
          });
        });
      });
    });
    return list;
  }, [form.studyId, studiesWithCourses]);

  // NOTE: formModuleOptions removed — materials now link to course (vak), not module

  const loadMaterials = async () => {
    setLoading(true);
    setError('');

    try {
      const params = { includeInactive: 'true' };
      if (filterCourse) {
        params.courseId = filterCourse;
      }

      const response = await moduleMaterialsAPI.getAll(params);
      setMaterials(response.materials || []);
    } catch (loadError) {
      setError(loadError.message || 'Leermateriaal laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
  }, [filterCourse]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset vak filter when study filter changes
  const handleFilterStudyChange = (value) => {
    setFilterStudy(value);
    setFilterCourse('');
  };

  const openCreate = () => {
    setError('');
    setOk('');
    setEditingId(null);
    setForm({
      ...emptyForm,
      studyId: filterStudy,
      courseId: filterCourse
    });
    setUploadFile(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setError('');
    setOk('');
    setEditingId(item.id);
    // Resolve studyId from the item's course
    const courseId = item.course?.id || item.course?._id || String(item.course || '');
    let studyId = '';
    outer: for (const study of studiesWithCourses) {
      for (const year of study.studyYears || []) {
        for (const mod of year.modules || []) {
          if ((mod.courses || []).some((c) => String(c.id || c._id) === courseId)) {
            studyId = study.id;
            break outer;
          }
        }
      }
    }
    setForm({
      studyId,
      courseId,
      title: item.title || '',
      description: item.description || '',
      type: item.type || 'document',
      articleContent: item.articleContent || '',
      externalUrl: item.externalUrl || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive !== false
    });
    setUploadFile(null);
    setShowModal(true);
  };

  const saveMaterial = async (event) => {
    event.preventDefault();
    setError('');
    setOk('');

    try {
      // studyId is a UI-only cascade helper; strip before sending
      const { studyId: _s, ...payload } = form;
      if (!payload.courseId) {
        setError('Kies een vak');
        return;
      }
      if (editingId) {
        await moduleMaterialsAPI.update(editingId, payload, uploadFile);
        setOk('Leermateriaal bijgewerkt');
      } else {
        await moduleMaterialsAPI.create(payload, uploadFile);
        setOk('Leermateriaal toegevoegd');
      }
      setShowModal(false);
      await loadMaterials();
    } catch (saveError) {
      setError(saveError.message || 'Opslaan mislukt');
    }
  };

  const removeMaterial = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit leermateriaal wilt verwijderen?')) return;
    setError('');
    setOk('');

    try {
      await moduleMaterialsAPI.delete(id);
      setOk('Leermateriaal verwijderd');
      await loadMaterials();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen mislukt');
    }
  };

  return (
    <div className="module-materials-page">
      <div className="page-header">
        <h1>Module Leermaterialen</h1>
        <button className="btn-primary" onClick={openCreate}>Leermateriaal toevoegen</button>
      </div>

      {error && <p className="mm-message mm-error">{error}</p>}
      {ok && <p className="mm-message mm-ok">{ok}</p>}

      <div className="mm-filter-row">
        <div className="mm-filter-group">
          <label>Studie</label>
          <select className="form-input" value={filterStudy} onChange={(e) => handleFilterStudyChange(e.target.value)}>
            <option value="">Alle studies</option>
            {[...studiesWithCourses]
              .sort((a, b) => getStudyOptionLabel(a).localeCompare(getStudyOptionLabel(b), 'nl'))
              .map((s) => (
              <option key={s.id} value={s.id}>{getStudyOptionLabel(s)}</option>
              ))}
          </select>
        </div>
        <div className="mm-filter-group">
          <label>Vak</label>
          <select
            className="form-input"
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            disabled={!filterStudy}
          >
            <option value="">Alle vakken</option>
            {filterCourseOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.code} – {c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p>Leermaterialen laden...</p>
      ) : (
        <div className="mm-list">
          {materials.map((item) => (
            <article className="mm-item" key={item.id}>
              <div className="mm-item-head">
                <h3>{item.title}</h3>
                <span className={`mm-type mm-type-${item.type}`}>{item.type}</span>
              </div>
              <p className="mm-muted">Vak: {item.course?.code ? `${item.course.code} - ${item.course.name}` : (item.module?.code ? `${item.module.code} - ${item.module.name}` : '-')}</p>
              {item.description && <p>{item.description}</p>}
              {item.type === 'article' && item.articleContent && (
                <div className="mm-article-preview" dangerouslySetInnerHTML={{ __html: item.articleContent }} />
              )}
              {item.externalUrl && (
                <p>
                  <a href={item.externalUrl} target="_blank" rel="noopener noreferrer">Open externe link</a>
                </p>
              )}
              {item.file?.filename && (
                <div className="mm-file-links">
                  <a href={moduleMaterialsAPI.viewFileUrl(item.id)} target="_blank" rel="noopener noreferrer">Bekijken</a>
                  <a href={moduleMaterialsAPI.downloadFileUrl(item.id)} target="_blank" rel="noopener noreferrer">Downloaden</a>
                </div>
              )}
              <div className="mm-actions">
                <button className="btn-secondary" onClick={() => openEdit(item)}>Bewerken</button>
                <button className="btn-danger" onClick={() => removeMaterial(item.id)}>Verwijderen</button>
              </div>
            </article>
          ))}
          {materials.length === 0 && <p className="no-data">Nog geen leermaterialen gevonden.</p>}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Leermateriaal bewerken' : 'Leermateriaal toevoegen'}>
        <form className="mm-form" onSubmit={saveMaterial}>
          <select
            className="form-input"
            value={form.studyId}
            onChange={(e) => setForm((prev) => ({ ...prev, studyId: e.target.value, courseId: '' }))}
          >
            <option value="">Kies studie (optioneel)</option>
            {[...(studies || [])]
              .sort((a, b) => getStudyOptionLabel(a).localeCompare(getStudyOptionLabel(b), 'nl'))
              .map((s) => (
              <option key={s.id} value={s.id}>{getStudyOptionLabel(s)}</option>
              ))}
          </select>

          <select
            className="form-input"
            value={form.courseId}
            onChange={(e) => setForm((prev) => ({ ...prev, courseId: e.target.value }))}
            required
          >
            <option value="">Kies vak</option>
            {formCourseOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.code} – {c.name}</option>
            ))}
          </select>

          <input
            className="form-input"
            placeholder="Titel"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <textarea
            className="form-textarea"
            placeholder="Beschrijving"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />

          <select
            className="form-input"
            value={form.type}
            onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="document">Document</option>
            <option value="media">Media</option>
            <option value="article">Artikel</option>
            <option value="link">Link</option>
          </select>

          {form.type === 'article' && (
            <RichTextEditor
              label="Artikel"
              value={form.articleContent}
              onChange={(value) => setForm((prev) => ({ ...prev, articleContent: value }))}
              placeholder="Schrijf hier het artikel..."
              minHeight={220}
            />
          )}

          {(form.type === 'link' || form.type === 'media') && (
            <input
              className="form-input"
              placeholder="Externe URL"
              value={form.externalUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, externalUrl: e.target.value }))}
            />
          )}

          {form.type !== 'article' && (
            <input
              className="form-input"
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
          )}

          <input
            className="form-input"
            type="number"
            placeholder="Volgorde"
            value={form.sortOrder}
            onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
          />

          <select
            className="form-input"
            value={String(form.isActive)}
            onChange={(e) => setForm((prev) => ({ ...prev, isActive: parseBool(e.target.value) }))}
          >
            <option value="true">Actief</option>
            <option value="false">Inactief</option>
          </select>

          <div className="form-actions">
            <button type="submit" className="btn-primary">Opslaan</button>
            <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Annuleren</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ModuleMaterials;
